import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card } from 'react-bootstrap';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import Forms from '../../components/Forms';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';
import ContractService from './ContractService';
import axios from '../../services/axios';

export default function ContractForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [selectOptions, setSelectOptions] = useState({
    products: [{ value: '', label: 'Selecione um produto...' }],
    customers: [{ value: '', label: 'Selecione um cliente...' }],
    companies: [{ value: '', label: 'Selecione uma empresa...' }],
    discounts: [{ value: '', label: 'Selecione...' }]
  });
  const [file, setFile] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const notification = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNewContract = !id;

  const [formValues, setFormValues] = useState({
    name: '',
    contract_type: 'Cliente',
    customer_id: '',
    company_id: '',
    product_id: '',
    value: '',
    contractual_discount_id: '',
    number: '',
    date_of_contract: '',
    status: 1
  });

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [productsRes, customersRes, companiesRes, discountsRes] = await Promise.all([
          axios.get('/products'),
          axios.get('/customers'),
          axios.get('/companies'),
          axios.get('/discounts')
        ]);
        const newOptions = {
          products: [{ value: '', label: 'Selecione um produto...' }],
          customers: [{ value: '', label: 'Selecione um cliente...' }],
          companies: [{ value: '', label: 'Selecione uma empresa...' }],
          discounts: [{ value: '', label: 'Selecione...' }]
        };

        [
          { data: productsRes.data, key: 'products' },
          { data: customersRes.data, key: 'customers' },
          { data: companiesRes.data, key: 'companies' },
          { data: discountsRes.data, key: 'discounts' }
        ].forEach(({ data, key }) => {
          if (data) {
            data.forEach(item => {
              newOptions[key].push({ value: item.id, label: item.name });
            });
          }
        });

        setSelectOptions(newOptions);
        if (id) {
          const data = await ContractService.getContract(id);
          setFormValues({
            name: data.name || '',
            contract_type: data.contract_type || 'Cliente',
            customer_id: data.customer_id || '',
            company_id: data.company_id || '',
            product_id: data.product_id || '',
            value: data.value || '0',
            contractual_discount_id: data.contractual_discount_id || '',
            number: data.number || '',
            date_of_contract: data.date_of_contract ? data.date_of_contract.split('T')[0] : '',
            status: data.status !== undefined ? data.status : 1
          });
        }
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
        setDataLoaded(true);
      }
    };

    loadData();
  }, [id]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Nome do contrato é obrigatório.')
      .max(255, 'O nome deve ter no máximo 255 caracteres.'),
    contract_type: Yup.string()
      .required('Tipo de contrato é obrigatório.')
      .oneOf(['Cliente', 'Empresarial'], 'Tipo de contrato inválido.'),
    customer_id: Yup.mixed()
      .when('contract_type', {
        is: 'Cliente',
        then: () => Yup.mixed().required('Cliente é obrigatório.'),
        otherwise: () => Yup.mixed().notRequired(),
      }),
    company_id: Yup.mixed()
      .when('contract_type', {
        is: 'Empresarial',
        then: () => Yup.mixed().required('Empresa é obrigatória.'),
        otherwise: () => Yup.mixed().notRequired(),
      }),
    product_id: Yup.mixed().required('Produto é obrigatório.'),
    value: Yup.number().min(0, 'O valor deve ser maior ou igual a zero.'),
    date_of_contract: Yup.date().max(new Date(), 'A data do contrato não pode ser no futuro.')
  });

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const dataToSubmit = { ...formValues };

      ['contractual_discount_id', 'value', 'number', 'date_of_contract'].forEach(field => {
        if (!dataToSubmit[field]) dataToSubmit[field] = null;
      });

      if (dataToSubmit.contract_type === 'Empresarial') {
        delete dataToSubmit.customer_id;
      } else {
        delete dataToSubmit.company_id;
      }

      let contractId = id;
      const action = isNewContract ? 'cadastrado' : 'atualizado';

      if (isNewContract) {
        const data = await ContractService.createContract(dataToSubmit);
        contractId = data.id;
      } else {
        await ContractService.updateContract(id, dataToSubmit);
      }

      if (file) {
        await ContractService.uploadContractFile(contractId, file);
      }

      navigate('/contact');
      notification(`Contrato ${action} com sucesso`, 'success');
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getFormFields = () => [
    {
      name: 'contract_type',
      id: 'contract_type',
      label: 'Tipo de Contrato',
      type: 'select',
      onChange: (e) => handleFieldChange('contract_type', e.target.value),
      colSize: 6,
      options: [
        { value: 'Cliente', label: 'Cliente' },
        { value: 'Empresarial', label: 'Empresarial' }
      ]
    },
    {
      name: formValues.contract_type === 'Cliente' ? 'customer_id' : 'company_id',
      id: formValues.contract_type === 'Cliente' ? 'customer_id' : 'company_id',
      label: formValues.contract_type === 'Cliente' ? 'Cliente' : 'Empresa',
      type: 'select',
      onChange: (e) => handleFieldChange(
        formValues.contract_type === 'Cliente' ? 'customer_id' : 'company_id',
        e.target.value
      ),
      colSize: 6,
      options: formValues.contract_type === 'Cliente'
        ? selectOptions.customers
        : selectOptions.companies
    },
    {
      name: 'name',
      id: 'name',
      label: 'Nome do Contrato',
      type: 'text',
      onChange: (e) => handleFieldChange('name', e.target.value),
      colSize: 6
    },
    {
      name: 'product_id',
      id: 'product_id',
      label: 'Produto',
      type: 'select',
      onChange: (e) => handleFieldChange('product_id', e.target.value),
      colSize: 6,
      options: selectOptions.products
    },
    {
      name: 'value',
      id: 'value',
      label: 'Valor',
      type: 'number',
      onChange: (e) => handleFieldChange('value', e.target.value),
      colSize: 4,
      additionalProps: { step: '0.01' }
    },
    {
      name: 'contractual_discount_id',
      id: 'contractual_discount_id',
      label: 'Desconto',
      type: 'select',
      onChange: (e) => handleFieldChange('contractual_discount_id', e.target.value),
      colSize: 4,
      options: selectOptions.discounts
    },
    {
      name: 'number',
      id: 'number',
      label: 'Número do Contrato',
      type: 'text',
      onChange: (e) => handleFieldChange('number', e.target.value),
      colSize: 4
    },
    {
      name: 'date_of_contract',
      id: 'date_of_contract',
      label: 'Data do Contrato',
      type: 'date',
      onChange: (e) => handleFieldChange('date_of_contract', e.target.value),
      colSize: 4
    },
    {
      name: 'status',
      id: 'status',
      label: 'Status',
      type: 'select',
      onChange: (e) => handleFieldChange('status', e.target.value),
      colSize: 4,
      options: [
        { value: 1, label: 'Ativo' },
        { value: 0, label: 'Inativo' }
      ]
    },
    {
      name: 'contract_file',
      id: 'contract_file',
      label: 'Arquivo do Contrato',
      type: 'file',
      onChange: (e) => e.target.files?.[0] && setFile(e.target.files[0]),
      colSize: 12,
      additionalProps: { accept: '.pdf,.doc,.docx' },
      info: 'Formatos permitidos: PDF, DOC, DOCX'
    }
  ];

  const actionText = isNewContract ? 'Cadastrar' : 'Atualizar';
  const pageTitle = `${actionText} Contrato`;

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title={pageTitle}
        pages={[{ link: '/contact', name: 'Contratos' }]}
      />
      <Card className="mb-5">
        <Card.Header>
          <h4 className="card-heading">
            {pageTitle}
            {!isNewContract && formValues.name && `: ${formValues.name}`}
          </h4>
        </Card.Header>
        <Card.Body>
          <ErrorProcessor error={apiErrors} />
          {(!id || dataLoaded) && (
            <Forms
              initialValues={formValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              fields={getFormFields()}
              btnSubmit={{
                variant: 'primary',
                type: 'submit',
                text: actionText,
              }}
              btnCancel={{
                variant: 'secondary',
                text: 'Cancelar',
                onClick: () => navigate('/contact')
              }}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}